import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardNavbar } from "@/components/DashboardNavbar";
import { StatusBadge, TypeBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { SERVICES, TOWNS, PROVINCES } from "@/lib/services";
import {
  Plus, FolderOpen, Database, FileSpreadsheet, FileText, Megaphone, Send, Trash2,
  Download, Eye, Search, Mail, MailCheck, Check, ChevronsUpDown, ChevronDown, ChevronUp, X, Paperclip, Users2,
  Briefcase, Building2, MapPin, Loader2, Users,
} from "lucide-react";

const OPP_TYPES = [
  { value: "RFQ", label: "RFQ (Request for Quotation)" },
  { value: "TENDER", label: "TENDER" },
  { value: "RFI", label: "RFI (Request for Information)" },
  { value: "RFP", label: "RFP (Request for Proposal)" },
];
const QUOTE_STATUSES = ["submitted", "shortlisted", "awarded", "rejected"];

async function downloadFile(path, fallbackName) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const disp = res.headers["content-disposition"] || "";
    const match = disp.match(/filename="?([^"]+)"?/);
    const name = match ? match[1] : fallbackName;
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  } catch (err) {
    toast.error("Export failed. Please try again.");
  }
}

export default function BuyerDashboard() {
  const { user } = useAuth();
  const [opps, setOpps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [notices, setNotices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [respOpp, setRespOpp] = useState(null);

  const emptyForm = { type: "RFQ", title: "", description: "", category: "", closing_date: "", closing_time: "", provinces: [], towns: [], sectors: [], requirements: "", attachments: [], broadcast: false, match_services: true };
  const [form, setForm] = useState(emptyForm);
  const [posting, setPosting] = useState(false);
  const [tab, setTab] = useState("post");

  const load = useCallback(async () => {
    const [op, cat, sup, nt, nf] = await Promise.all([
      api.get("/opportunities", { params: { mine: true } }),
      api.get("/categories"),
      api.get("/buyer/suppliers"),
      api.get("/notices"),
      api.get("/notifications"),
    ]);
    setOpps(op.data); setCategories(cat.data); setSuppliers(sup.data); setNotices(nt.data); setNotifications(nf.data);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const postOpportunity = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.category || !form.closing_date) {
      toast.error("Title, description, category and closing date are required"); return;
    }
    setPosting(true);
    try {
      await api.post("/opportunities", form);
      toast.success("Opportunity published & suppliers notified");
      setForm(emptyForm); await load(); setTab("mine");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const deleteOpp = async (id) => {
    try { await api.delete(`/opportunities/${id}`); toast.success("Opportunity deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const closeOpp = async (id) => {
    try { await api.patch(`/opportunities/${id}/close`); toast.success("Opportunity closed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <DashboardNavbar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="buyer-welcome">Welcome, {user?.name || user?.company_name}</h1>
          <p className="mt-1 text-slate-500">{user?.company_name ? `${user.company_name} · ` : ""}Buyer Dashboard</p>

          <Tabs value={tab} onValueChange={setTab} className="mt-8">
            <TabsList data-testid="buyer-tabs" className="flex-wrap">
              <TabsTrigger value="post" data-testid="tab-post">Post Opportunity</TabsTrigger>
              <TabsTrigger value="mine" data-testid="tab-mine">My Opportunities</TabsTrigger>
              <TabsTrigger value="smme" data-testid="tab-smme">SMME Database</TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
              <TabsTrigger value="notices" data-testid="tab-notices">Notice Board</TabsTrigger>
              <TabsTrigger value="notify" data-testid="tab-notify">Notifications send</TabsTrigger>
            </TabsList>

            <TabsContent value="post" className="mt-6">
              <PostOpportunity form={form} setForm={setForm} sectors={categories} onSubmit={postOpportunity} posting={posting} />
            </TabsContent>

            <TabsContent value="mine" className="mt-6">
              <MyOpportunities opps={opps} onView={setRespOpp} onClose={closeOpp} onDelete={deleteOpp} />
            </TabsContent>

            <TabsContent value="smme" className="mt-6">
              <SmmeDatabase suppliers={suppliers} sectors={categories} />
            </TabsContent>

            <TabsContent value="reports" className="mt-6">
              <ScoreCard onNavigate={setTab} />
            </TabsContent>

            <TabsContent value="notices" className="mt-6">
              <NoticeBoard notices={notices} user={user} sectors={categories} reload={load} />
            </TabsContent>

            <TabsContent value="notify" className="mt-6">
              <NotificationsSend suppliers={suppliers} history={notifications} reload={load} />
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>

      {respOpp && <ResponsesDialog opp={respOpp} onClose={() => { setRespOpp(null); load(); }} />}
    </div>
  );
}

/* ---------- Multi-select combobox ---------- */
function MultiCombobox({ placeholder, options, selected, onToggle, testid, allowCustom }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const sel = selected || [];
  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));
  const hasExact = [...options, ...sel].some((o) => o.toLowerCase() === query.trim().toLowerCase());
  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" className={`mt-1.5 w-full justify-between font-normal ${sel.length ? "" : "text-slate-400"}`} data-testid={`${testid}-trigger`}>
            <span className="truncate">{sel.length ? `${sel.length} selected` : placeholder}</span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput value={query} onValueChange={setQuery} placeholder="Type or search…" data-testid={`${testid}-search`} />
            <CommandList>
              {filtered.length === 0 && !query.trim() && <CommandEmpty>No match.</CommandEmpty>}
              {allowCustom && query.trim() && !hasExact && (
                <CommandItem value={`__use__${query}`} onSelect={() => { onToggle(query.trim()); setQuery(""); }} data-testid={`${testid}-custom`}>
                  <Plus className="mr-2 h-4 w-4 text-amber-600" /> Add "{query.trim()}"
                </CommandItem>
              )}
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem key={o} value={o} onSelect={() => onToggle(o)} data-testid={`${testid}-option-${o}`}>
                    <Check className={`mr-2 h-4 w-4 ${sel.includes(o) ? "opacity-100" : "opacity-0"}`} />
                    {o}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {sel.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
          {sel.map((o) => (
            <span key={o} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 py-0.5 pl-2.5 pr-1 text-xs text-slate-700">
              {o}
              <button type="button" onClick={() => onToggle(o)} className="rounded-full p-0.5 text-slate-400 hover:bg-red-100 hover:text-red-600" data-testid={`${testid}-remove-${o}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Expandable recipients list ---------- */
function RecipientsReveal({ recipients, testid }) {
  const [open, setOpen] = useState(false);
  const list = recipients || [];
  if (!list.length) return null;
  return (
    <div className="mt-2" data-testid={`${testid}-wrap`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline" data-testid={`${testid}-toggle`}>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {open ? "Hide" : "View"} the SMMEs who will be emailed ({list.length})
      </button>
      {open && (
        <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white" data-testid={`${testid}-list`}>
          {list.map((r, i) => (
            <div key={`${r.name}-${r.town}-${i}`} className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-xs last:border-0" data-testid={`${testid}-item-${i}`}>
              <span className="truncate font-medium text-slate-700">{r.name}</span>
              <span className="shrink-0 pl-2 text-slate-400">{r.town}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Searchable multi-select checkbox list ---------- */
function CheckboxMultiList({ options, selected, onToggle, placeholder, testid, allowCustom }) {
  const [query, setQuery] = useState("");
  const sel = selected || [];
  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));
  const hasExact = [...options, ...sel].some((o) => o.toLowerCase() === query.trim().toLowerCase());
  return (
    <div className="mt-1.5">
      {sel.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
          {sel.map((o) => (
            <span key={o} className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 py-0.5 pl-2.5 pr-1 text-xs text-amber-900" data-testid={`${testid}-chip-${o}`}>
              {o}
              <button type="button" onClick={() => onToggle(o)} className="rounded-full p-0.5 text-amber-600 hover:bg-amber-100" data-testid={`${testid}-remove-${o}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <Input placeholder={placeholder} value={query} onChange={(e) => setQuery(e.target.value)} data-testid={`${testid}-search`} />
      <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200" data-testid={`${testid}-list`}>
        {allowCustom && query.trim() && !hasExact && (
          <button type="button" onClick={() => { onToggle(query.trim()); setQuery(""); }} className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm text-amber-700 hover:bg-amber-50" data-testid={`${testid}-custom`}>
            <Plus className="h-4 w-4" /> Add "{query.trim()}"
          </button>
        )}
        {filtered.length === 0 && !query.trim() && <div className="px-3 py-2 text-sm text-slate-400">No match.</div>}
        {filtered.map((o) => {
          const checked = sel.includes(o);
          return (
            <label key={o} className="flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-sm last:border-0 hover:bg-slate-50" data-testid={`${testid}-option-${o}`}>
              <Checkbox checked={checked} onCheckedChange={() => onToggle(o)} />
              <span className="text-slate-700">{o}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Post Opportunity ---------- */
function PostOpportunity({ form, setForm, sectors, onSubmit, posting }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (key, val) => setForm((f) => {
    const a = f[key] || [];
    return { ...f, [key]: a.includes(val) ? a.filter((x) => x !== val) : [...a, val] };
  });
  const [catQuery, setCatQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [audience, setAudience] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const filteredCats = SERVICES.filter((c) => c.toLowerCase().includes(catQuery.toLowerCase()));

  useEffect(() => {
    let active = true;
    api.post("/opportunities/audience-preview", {
      provinces: form.provinces, towns: form.towns, category: form.category,
      match_services: true, broadcast: form.broadcast,
    }).then((r) => { if (active) { setAudience(r.data.count); setRecipients(r.data.recipients || []); } }).catch(() => { if (active) { setAudience(null); setRecipients([]); } });
    return () => { active = false; };
  }, [form.provinces, form.towns, form.category, form.broadcast]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} exceeds 25MB`); continue; }
        const fd = new FormData();
        fd.append("file", file); fd.append("doc_type", "opportunity");
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ file_id: r.data.id, filename: r.data.original_filename, size: r.data.size });
      }
      if (uploaded.length) { setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] })); toast.success("File(s) attached"); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const removeAtt = (id) => setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((a) => a.file_id !== id) }));

  return (
    <div className="max-w-3xl rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Plus className="h-5 w-5 text-amber-600" /> Post a new opportunity</h3>
      <p className="text-sm text-slate-500">Target the right SMMEs by area and sector — only matching, approved SMMEs are emailed.</p>
      <div className="mt-5 space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Opportunity type <span className="text-red-500">*</span></Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="mt-1.5" data-testid="opp-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OPP_TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`opp-type-${t.value}`}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Closing date <span className="text-red-500">*</span></Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <Input type="date" value={form.closing_date} onChange={(e) => set("closing_date", e.target.value)} data-testid="opp-closing-input" />
              <Input type="time" value={form.closing_time} onChange={(e) => set("closing_time", e.target.value)} data-testid="opp-closing-time-input" />
            </div>
          </div>
        </div>
        <div>
          <Label>Title <span className="text-red-500">*</span></Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" placeholder="e.g. Supply of safety boots" data-testid="opp-title-input" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Category <span className="text-red-500">*</span> (choose one)</Label>
            <span className="text-xs font-medium text-slate-500" data-testid="opp-category-count">{form.category ? "1" : "0"}/1 selected</span>
          </div>
          {form.category && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="opp-selected-category">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 py-1 pl-3 pr-1.5 text-sm text-amber-900" data-testid={`opp-selected-category-${form.category}`}>
                {form.category}
                <button type="button" onClick={() => set("category", "")} className="rounded-full p-0.5 text-amber-600 hover:bg-amber-100" data-testid="opp-remove-category">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            </div>
          )}
          <Input placeholder="Search category…" value={catQuery} onChange={(e) => setCatQuery(e.target.value)} className="mt-3" data-testid="opp-category-search" />
          <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200" data-testid="opp-category-list">
            {filteredCats.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No category found.</div>}
            {filteredCats.map((c) => {
              const checked = form.category === c;
              return (
                <label key={c} className="flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-sm last:border-0 hover:bg-slate-50" data-testid={`opp-category-option-${c}`}>
                  <Checkbox checked={checked} onCheckedChange={() => set("category", checked ? "" : c)} />
                  <span className="text-slate-700">{c}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div>
          <Label>Description <span className="text-red-500">*</span></Label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1.5" placeholder="Scope of work, quantities, specifications…" data-testid="opp-description-input" />
        </div>

        {/* Target area */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Target area</h4>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Province(s)</Label>
                <span className="text-xs font-medium text-slate-500" data-testid="opp-province-count">{(form.provinces || []).length} selected</span>
              </div>
              <CheckboxMultiList options={PROVINCES} selected={form.provinces} onToggle={(v) => toggleArr("provinces", v)} placeholder="Search province…" testid="opp-province" />
            </div>
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Town(s) / Village(s)</Label>
                <span className="text-xs font-medium text-slate-500" data-testid="opp-location-count">{(form.towns || []).length} selected</span>
              </div>
              <CheckboxMultiList options={TOWNS} selected={form.towns} onToggle={(v) => toggleArr("towns", v)} placeholder="Search town / village…" testid="opp-location" allowCustom />
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Requirements (compliance, certifications, deliverables)</Label>
          <Textarea value={form.requirements} onChange={(e) => set("requirements", e.target.value)} rows={4} className="mt-1.5" placeholder="e.g. Valid BBBEE certificate, SARS tax clearance, delivery within 14 days…" data-testid="opp-requirements-input" />
        </div>

        {/* Attachments */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Attachments (RFQ document, scope of work, BOQ — max 25MB each)</Label>
          <label className="mt-1.5 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-4 py-6 text-sm text-slate-500 hover:border-amber-400 hover:text-amber-700" data-testid="opp-attach-dropzone">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} {uploading ? "Uploading…" : "Click to choose files from your computer"}
            <input type="file" multiple className="hidden" onChange={onFiles} disabled={uploading} data-testid="opp-attach-input" />
          </label>
          {(form.attachments || []).length > 0 && (
            <div className="mt-2 space-y-1.5" data-testid="opp-attach-list">
              {(form.attachments || []).map((a) => (
                <div key={a.file_id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid={`opp-attach-${a.file_id}`}>
                  <span className="flex items-center gap-2 truncate text-slate-700"><FileText className="h-4 w-4 text-amber-600" /> {a.filename}</span>
                  <button type="button" onClick={() => removeAtt(a.file_id)} className="rounded-full p-1 text-slate-400 hover:bg-red-100 hover:text-red-600" data-testid={`opp-attach-remove-${a.file_id}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Who will be notified */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Who will be notified</h4>
              <p className="text-sm italic text-slate-500">SMMEs are matched automatically from the Category and Target Area you chose above.</p>
            </div>
            <span className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="opp-audience-count">
              <Users2 className="h-3.5 w-3.5" /> {audience == null ? "…" : `${audience} SMME(s)`} will receive an email
            </span>
          </div>
          <RecipientsReveal recipients={recipients} testid="opp-recipients" />
          <label className="mt-4 flex items-start gap-2.5 rounded-lg border border-slate-200 bg-white p-3">
            <Checkbox checked={form.broadcast} onCheckedChange={(v) => set("broadcast", !!v)} data-testid="opp-broadcast" className="mt-0.5" />
            <span className="text-sm">
              <span className="font-semibold text-slate-800">Broadcast to every SMME</span>
              <span className="block text-xs text-slate-500">Ignore the category and target-area filters — useful for niche tenders that need maximum reach.</span>
            </span>
          </label>
        </div>

        <Button onClick={onSubmit} disabled={posting || uploading} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="post-opp-button">
          {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Post opportunity
        </Button>
      </div>
    </div>
  );
}

/* ---------- My Opportunities ---------- */
function MyOpportunities({ opps, onView, onClose, onDelete }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="buyer-opps-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr><th className="px-5 py-3">Reference</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Title</th><th className="px-5 py-3">Closing</th><th className="px-5 py-3">Responses</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {opps.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500" data-testid="no-opps">You have not posted any opportunities yet.</td></tr>}
          {opps.map((o) => (
            <tr key={o.id} className="hover:bg-slate-50" data-testid={`buyer-opp-row-${o.id}`}>
              <td className="px-5 py-3 font-mono text-xs text-slate-500">{o.reference}</td>
              <td className="px-5 py-3"><TypeBadge type={o.type} /></td>
              <td className="px-5 py-3 font-medium text-slate-800">{o.title}</td>
              <td className="px-5 py-3 text-slate-600">{o.closing_date}</td>
              <td className="px-5 py-3 font-mono">{o.response_count}</td>
              <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
              <td className="px-5 py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => onView(o)} className="gap-1" data-testid={`view-responses-${o.id}`}><Eye className="h-3.5 w-3.5" /> Responses</Button>
                  {o.status === "open" && <Button size="sm" variant="ghost" onClick={() => onClose(o.id)} className="text-slate-600" data-testid={`close-opp-${o.id}`}>Close</Button>}
                  <Button size="sm" variant="ghost" onClick={() => onDelete(o.id)} className="text-red-600" data-testid={`delete-opp-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Responses dialog ---------- */
function ResponsesDialog({ opp, onClose }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/opportunities/${opp.id}/quotes`); setQuotes(r.data); }
    catch { toast.error("Could not load responses"); }
    finally { setLoading(false); }
  }, [opp.id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/quotes/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="responses-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{opp.title}</DialogTitle>
          <DialogDescription>{opp.reference} · {quotes.length} response(s)</DialogDescription>
        </DialogHeader>
        {loading && <div className="py-10 text-center text-slate-500"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>}
        {!loading && quotes.length === 0 && <p className="py-10 text-center text-slate-500" data-testid="no-responses">No responses received yet.</p>}
        <div className="space-y-3">
          {quotes.map((q) => (
            <div key={q.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`response-card-${q.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-display font-bold text-slate-900">{q.supplier_company}</h4>
                  <p className="text-xs text-slate-500">BBBEE: {q.supplier_bbbee || "—"} · {new Date(q.created_at).toLocaleDateString()}</p>
                </div>
                <StatusBadge status={q.status} />
              </div>
              {q.amount != null && <p className="mt-2 text-sm text-slate-700">Quoted amount: <span className="font-semibold">R {Number(q.amount).toLocaleString()}</span></p>}
              {q.message && <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{q.message}</p>}
              {q.document && <a href={`${API}/files/${q.document.id}/download`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid={`response-doc-${q.id}`}><Download className="h-3.5 w-3.5" /> {q.document.filename}</a>}
              <div className="mt-3 flex flex-wrap gap-2">
                {QUOTE_STATUSES.map((s) => (
                  <Button key={s} size="sm" variant={q.status === s ? "default" : "outline"} onClick={() => setStatus(q.id, s)} className="capitalize" data-testid={`set-status-${s}-${q.id}`}>{s}</Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- SMME Database ---------- */
const BBBEE_LEVELS = ["Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6", "Level 7", "Level 8", "Non-compliant"];
const ALL = "__all__";

function SmmeDatabase({ suppliers, sectors }) {
  const blank = { company: "", email: "", keyword: "", sector: [], service: ALL, province: ALL, town: [], bbbee: ALL };
  const [draft, setDraft] = useState(blank);
  const [applied, setApplied] = useState(blank);
  const [detail, setDetail] = useState(null);
  const setD = (k, v) => setDraft((f) => ({ ...f, [k]: v }));
  const toggleD = (field, val) => setDraft((f) => {
    const a = f[field] || [];
    return { ...f, [field]: a.includes(val) ? a.filter((x) => x !== val) : [...a, val] };
  });

  const matches = (s) => {
    const inc = (val, q) => !q || String(val || "").toLowerCase().includes(q.trim().toLowerCase());
    const eq = (val, q) => q === ALL || String(val || "") === q;
    const inArr = (val, arr) => !arr || arr.length === 0 || arr.includes(String(val || ""));
    const svc = applied.service === ALL || (s.services || []).includes(applied.service);
    const kw = !applied.keyword || [s.company_name, s.description, s.sector, (s.services || []).join(" ")]
      .some((v) => String(v || "").toLowerCase().includes(applied.keyword.trim().toLowerCase()));
    return inc(s.company_name, applied.company) && inc(s.email, applied.email) && kw
      && inArr(s.sector, applied.sector) && svc && eq(s.province, applied.province)
      && inArr(s.town, applied.town) && eq(s.bbbee_level, applied.bbbee);
  };
  const filtered = suppliers.filter(matches);

  const exportParams = () => {
    const p = new URLSearchParams();
    if (applied.company) p.set("company", applied.company);
    if (applied.email) p.set("email", applied.email);
    if (applied.keyword) p.set("keyword", applied.keyword);
    (applied.sector || []).forEach((v) => p.append("sector", v));
    if (applied.service !== ALL) p.set("service", applied.service);
    if (applied.province !== ALL) p.set("province", applied.province);
    (applied.town || []).forEach((v) => p.append("town", v));
    if (applied.bbbee !== ALL) p.set("bbbee", applied.bbbee);
    return p.toString();
  };
  const doExport = (fmt) => {
    const qs = exportParams();
    downloadFile(`/export/suppliers?format=${fmt}${qs ? "&" + qs : ""}`, `smme-database.${fmt === "pdf" ? "pdf" : "xlsx"}`);
  };
  const Dropdown = ({ label, field, placeholder, options, testid }) => (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</Label>
      <Select value={draft[field]} onValueChange={(v) => setD(field, v)}>
        <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={ALL}>{placeholder}</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="smme-filters">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Company name</Label>
            <Input value={draft.company} onChange={(e) => setD("company", e.target.value)} placeholder="e.g. Bokamoso Trading" className="mt-1.5" data-testid="smme-company-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Email address</Label>
            <Input value={draft.email} onChange={(e) => setD("email", e.target.value)} placeholder="e.g. kabelo@bokamoso.co.za" className="mt-1.5" data-testid="smme-email-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Keyword</Label>
            <Input value={draft.keyword} onChange={(e) => setD("keyword", e.target.value)} placeholder="Business name, service or description" className="mt-1.5" data-testid="smme-keyword-input" />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Sector</Label>
            <MultiCombobox placeholder="All sectors" options={sectors || []} selected={draft.sector} onToggle={(v) => toggleD("sector", v)} testid="smme-sector" allowCustom />
          </div>
          <Dropdown label="Service offered" field="service" placeholder="Any service" options={SERVICES} testid="smme-service-select" />
          <Dropdown label="Province" field="province" placeholder="All" options={PROVINCES} testid="smme-province-select" />
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">City / Town / Village</Label>
            <MultiCombobox placeholder="All towns / villages" options={TOWNS} selected={draft.town} onToggle={(v) => toggleD("town", v)} testid="smme-town" allowCustom />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-56">
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">B-BBEE level</Label>
            <Select value={draft.bbbee} onValueChange={(v) => setD("bbbee", v)}>
              <SelectTrigger className="mt-1.5" data-testid="smme-bbbee-select"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {BBBEE_LEVELS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => setApplied(draft)} className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="smme-search-button"><Search className="h-4 w-4" /> Search</Button>
          <Button variant="outline" onClick={() => { setDraft(blank); setApplied(blank); }} className="gap-2" data-testid="smme-reset-button"><X className="h-4 w-4" /> Reset filters</Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-slate-700" data-testid="smme-count">{filtered.length} SMME{filtered.length === 1 ? "" : "s"} found</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => doExport("pdf")} className="gap-1" data-testid="export-smme-pdf"><FileText className="h-4 w-4 text-red-600" /> Export to PDF</Button>
            <Button variant="outline" onClick={() => doExport("xlsx")} className="gap-1" data-testid="export-smme-excel"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Export to Excel</Button>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="smme-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Company</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Province</th><th className="px-4 py-3">Sector</th><th className="px-4 py-3">BBBEE</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500" data-testid="no-smme">No SMMEs match your filters.</td></tr>}
              {filtered.map((s) => (
                <tr key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(s)} data-testid={`smme-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{s.company_name}<div className="text-xs font-normal text-slate-500">{s.email}</div></td>
                  <td className="px-4 py-3 text-slate-600">{s.contact_person || s.name || "—"}<div className="text-xs text-slate-400">{s.phone || ""}</div></td>
                  <td className="px-4 py-3 text-slate-600">{s.province || "—"}<div className="text-xs text-slate-400">{s.town || ""}</div></td>
                  <td className="px-4 py-3 text-slate-600">{s.sector || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.bbbee_level || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {detail && <SmmeProfileDialog s={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function SmmeProfileDialog({ s, onClose }) {
  const Field = ({ label, value, mono }) => (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-400">{label}</Label>
      <p className={`text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value || "—"}</p>
    </div>
  );
  const docs = s.documents || [];
  const services = s.services || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="smme-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">{s.company_name}</DialogTitle>
          <DialogDescription>{s.email}{s.phone ? ` · ${s.phone}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Contact person" value={s.contact_person || s.name} />
          <Field label="Sector" value={s.sector} />
          <Field label="Province" value={s.province} />
          <Field label="Town / Village" value={s.town} />
          <Field label="BBBEE level" value={s.bbbee_level} mono />
          <Field label="CIPC number" value={s.registration_number} mono />
          <Field label="Tax number" value={s.tax_number} mono />
        </div>
        {s.description && (
          <div className="mt-1">
            <Label className="text-xs uppercase tracking-wide text-slate-400">About</Label>
            <p className="text-sm text-slate-700">{s.description}</p>
          </div>
        )}
        <div className="mt-1">
          <Label className="text-xs uppercase tracking-wide text-slate-400">Services</Label>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="smme-profile-services">
            {services.length === 0 && <span className="text-sm text-slate-500">—</span>}
            {services.map((v) => <span key={v} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-700">{v}</span>)}
          </div>
        </div>
        <div className="mt-2">
          <Label className="text-xs uppercase tracking-wide text-slate-400">Compliance documents</Label>
          <div className="mt-1.5 divide-y divide-slate-100" data-testid="smme-profile-docs">
            {docs.length === 0 && <p className="py-2 text-sm text-slate-500">No documents uploaded.</p>}
            {docs.map((d) => (
              <div key={d.file_id} className="flex items-center justify-between py-2">
                <span className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-amber-600" /> {d.doc_type}{d.expiry_date ? ` · expires ${d.expiry_date}` : ""}</span>
                <a href={`${API}/files/${d.file_id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid={`smme-profile-doc-${d.file_id}`}><Download className="h-3.5 w-3.5" /> View</a>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Scorecard ---------- */
function ScoreCard({ onNavigate }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/scorecard").then((r) => setData(r.data)).catch(() => {}); }, []);
  const d = data || {};
  const cards = [
    { key: "posted", icon: Briefcase, label: "Posted opportunities for the month", value: d.posted_opportunities?.month ?? 0, sub: `${d.posted_opportunities?.total ?? 0} total since joining`, go: "mine" },
    { key: "buyers", icon: Briefcase, label: "Total buyers on the portal", value: d.total_buyers?.total ?? 0, sub: `${d.total_buyers?.month ?? 0} joined this month`, go: null },
    { key: "smme-month", icon: Building2, label: "SMMEs registered for the month", value: d.smmes_registered?.month ?? 0, sub: `${d.smmes_registered?.total_verified ?? 0} total verified SMMEs`, go: "smme" },
    { key: "smme-total", icon: Building2, label: "Total SMMEs in database", value: d.total_smmes?.total ?? 0, sub: "Click to browse & filter the directory", go: "smme" },
    { key: "notif", icon: Mail, label: "Notifications sent for the month", value: d.notifications?.month ?? 0, sub: `${d.notifications?.total ?? 0} total sent from your opportunities`, go: "notify" },
  ];
  return (
    <div className="space-y-5" data-testid="scorecard">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Activity for {d.month_label || "this month"}</p>
          <h3 className="mt-1 font-display text-2xl font-bold text-slate-900">Your procurement scorecard</h3>
          <p className="text-sm text-slate-500">Click any card below to see the underlying data.</p>
        </div>
        <Button variant="outline" onClick={() => downloadFile("/export/suppliers?format=pdf", "smme-database.pdf")} className="gap-1" data-testid="scorecard-export-pdf"><Download className="h-4 w-4" /> Export to PDF</Button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => c.go ? onNavigate(c.go) : null}
            className={`flex flex-col rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md ${c.go ? "cursor-pointer" : "cursor-default"}`}
            data-testid={`scorecard-card-${c.key}`}
          >
            <div className="flex items-start justify-between">
              <span className="text-xs font-bold uppercase leading-tight tracking-wide text-slate-500">{c.label}</span>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100"><c.icon className="h-4 w-4 text-slate-600" /></span>
            </div>
            <div className="mt-3 font-display text-4xl font-extrabold text-slate-900" data-testid={`scorecard-value-${c.key}`}>{c.value}</div>
            <div className="mt-1 text-xs text-slate-500">{c.sub}</div>
            <span className={`mt-4 text-xs font-semibold ${c.go ? "text-blue-600" : "text-slate-300"}`}>VIEW DETAILS →</span>
          </button>
        ))}
      </div>

      <CommunityFootprint footprint={d.footprint || []} totals={d.footprint_totals || {}} />
      <SectorFootprint data={d.sector_footprint || []} totals={d.sector_footprint_totals || {}} />
    </div>
  );
}

const FP_COLORS = ["#0f766e", "#2563eb", "#7c3aed", "#ea580c", "#059669", "#db2777", "#dc2626", "#0891b2", "#a16207", "#4f46e5", "#0d9488", "#ca8a04", "#9333ea", "#e11d48", "#15803d"];

/* ---------- Sector footprint ---------- */
function SectorFootprint({ data, totals }) {
  if (!data.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="sector-footprint">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><Briefcase className="h-4 w-4" /> Sector footprint</div>
        <h3 className="mt-1 font-display text-xl font-bold text-slate-900">SMMEs registered per sector</h3>
        <p className="mt-6 rounded-lg border border-dashed border-slate-300 py-10 text-center text-slate-500" data-testid="sector-footprint-empty">No sector data captured yet. SMMEs appear here once they set their sector in their profile.</p>
      </div>
    );
  }
  const max = Math.max(1, ...data.map((f) => f.count));
  const chart = data.slice(0, 13);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="sector-footprint">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><Briefcase className="h-4 w-4" /> Sector footprint</div>
          <h3 className="mt-1 font-display text-xl font-bold text-slate-900">SMMEs registered per sector</h3>
          <p className="text-sm text-slate-500">Which industries the network already covers — useful for procurement targeting.</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid="sector-total-smmes"><Building2 className="h-3.5 w-3.5" /> {totals.smmes ?? 0} SMMEs</span>
          <span className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-blue-800" data-testid="sector-total-sectors"><Briefcase className="h-3.5 w-3.5" /> {totals.sectors ?? 0} sectors</span>
        </div>
      </div>

      <div className="mt-6 flex items-end gap-4 overflow-x-auto pb-2" data-testid="sector-chart">
        {chart.map((f, i) => (
          <div key={f.sector} className="flex flex-col items-center" style={{ minWidth: 56 }}>
            <div className="flex h-44 flex-col items-center justify-end">
              <span className="mb-1 text-xs font-bold text-slate-700">{f.count}</span>
              <div className="w-9 rounded-t" style={{ height: `${Math.max(4, Math.round((f.count / max) * 150))}px`, background: FP_COLORS[i % FP_COLORS.length] }} />
            </div>
            <span className="mt-2 w-16 truncate text-center text-[10px] text-slate-500" title={f.sector}>{f.sector}</span>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="sector-list">
        {data.map((f, i) => (
          <div key={f.sector} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3" data-testid={`sector-row-${f.sector}`}>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: FP_COLORS[i % FP_COLORS.length] }} />
              <div className="text-sm font-semibold text-slate-800">{f.sector}</div>
            </div>
            <span className="font-display text-lg font-bold text-slate-900">{f.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Community footprint ---------- */
function CommunityFootprint({ footprint, totals }) {
  if (!footprint.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="community-footprint">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><MapPin className="h-4 w-4" /> Community footprint</div>
        <h3 className="mt-1 font-display text-xl font-bold text-slate-900">SMMEs registered per village</h3>
        <p className="mt-6 rounded-lg border border-dashed border-slate-300 py-10 text-center text-slate-500" data-testid="footprint-empty">No town/village data captured yet. SMMEs appear here once they set their town in their profile.</p>
      </div>
    );
  }
  const max = Math.max(1, ...footprint.map((f) => f.count));
  const chart = footprint.slice(0, 15);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="community-footprint">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><MapPin className="h-4 w-4" /> Community footprint</div>
          <h3 className="mt-1 font-display text-xl font-bold text-slate-900">SMMEs registered per village</h3>
          <p className="text-sm text-slate-500">Where the network is coming from — great for community reporting back.</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid="footprint-total-smmes"><Building2 className="h-3.5 w-3.5" /> {totals.smmes ?? 0} SMMEs</span>
          <span className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-blue-800" data-testid="footprint-total-villages"><MapPin className="h-3.5 w-3.5" /> {totals.villages ?? 0} villages</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700" data-testid="footprint-total-provinces">{totals.provinces ?? 0} provinces</span>
        </div>
      </div>

      <div className="mt-6 flex items-end gap-4 overflow-x-auto pb-2" data-testid="footprint-chart">
        {chart.map((f) => (
          <div key={f.town} className="flex flex-col items-center" style={{ minWidth: 52 }}>
            <div className="flex h-44 flex-col items-center justify-end">
              <span className="mb-1 text-xs font-bold text-slate-700">{f.count}</span>
              <div className="w-9 rounded-t bg-teal-700" style={{ height: `${Math.max(4, Math.round((f.count / max) * 150))}px` }} />
            </div>
            <span className="mt-2 w-16 truncate text-center text-[10px] text-slate-500" title={f.town}>{f.town}</span>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="footprint-list">
        {footprint.map((f) => (
          <div key={f.town} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3" data-testid={`footprint-row-${f.town}`}>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-teal-600" />
              <div>
                <div className="text-sm font-semibold text-slate-800">{f.town}</div>
                <div className="text-xs text-slate-400">{f.province || "—"}</div>
              </div>
            </div>
            <span className="font-display text-lg font-bold text-slate-900">{f.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Notice Board ---------- */
function NoticeBoard({ notices, user, sectors, reload }) {
  const blank = { title: "", body: "", category: "Announcements", target_category: "", target_towns: [], target_sectors: [] };
  const [form, setForm] = useState(blank);
  const [audience, setAudience] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [catQuery, setCatQuery] = useState("");
  const filteredCats = SERVICES.filter((c) => c.toLowerCase().includes(catQuery.toLowerCase()));
  const toggle = (key, val) => setForm((f) => {
    const a = f[key] || [];
    return { ...f, [key]: a.includes(val) ? a.filter((x) => x !== val) : [...a, val] };
  });
  useEffect(() => {
    let active = true;
    api.post("/notices/audience-preview", { target_towns: form.target_towns, target_category: form.target_category, match_services: true })
      .then((r) => { if (active) { setAudience(r.data.count); setRecipients(r.data.recipients || []); } }).catch(() => { if (active) { setAudience(null); setRecipients([]); } });
    return () => { active = false; };
  }, [form.target_towns, form.target_category]);
  const post = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and message are required"); return; }
    try { await api.post("/notices", { ...form, match_services: true }); setForm(blank); toast.success("Notice posted"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/notices/${id}`); toast.success("Notice removed"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
      <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Megaphone className="h-5 w-5 text-amber-600" /> Post a notice</h3>
        <p className="text-sm text-slate-500">Leave the targets empty to reach every SMME, or narrow it down by location and sector.</p>
        <div className="mt-4 space-y-3">
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="notice-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Meetings">Meetings</SelectItem>
              <SelectItem value="Announcements">Announcements</SelectItem>
              <SelectItem value="Proposals">Proposals</SelectItem>
            </SelectContent>
          </Select>
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Notice title" data-testid="notice-title-input" />
          <Textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={4} placeholder="Write your announcement…" data-testid="notice-body-input" />
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Target location(s)</Label>
            <CheckboxMultiList options={TOWNS} selected={form.target_towns} onToggle={(v) => toggle("target_towns", v)} placeholder="Search location… (leave empty for all)" testid="notice-town" allowCustom />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Target category</Label>
              <span className="text-xs font-medium text-slate-500" data-testid="notice-category-count">{form.target_category ? "1" : "0"}/1 selected</span>
            </div>
            {form.target_category && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="notice-selected-category">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 py-1 pl-3 pr-1.5 text-sm text-amber-900">
                  {form.target_category}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, target_category: "" }))} className="rounded-full p-0.5 text-amber-600 hover:bg-amber-100" data-testid="notice-remove-category"><X className="h-3.5 w-3.5" /></button>
                </span>
              </div>
            )}
            <Input placeholder="Search category… (leave empty for all)" value={catQuery} onChange={(e) => setCatQuery(e.target.value)} className="mt-2" data-testid="notice-category-search" />
            <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200" data-testid="notice-category-list">
              {filteredCats.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No category found.</div>}
              {filteredCats.map((c) => {
                const checked = form.target_category === c;
                return (
                  <label key={c} className="flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-sm last:border-0 hover:bg-slate-50" data-testid={`notice-category-option-${c}`}>
                    <Checkbox checked={checked} onCheckedChange={() => setForm((f) => ({ ...f, target_category: checked ? "" : c }))} />
                    <span className="text-slate-700">{c}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="rounded-lg bg-amber-50 px-3 py-2" data-testid="notice-audience-count">
            <div className="flex items-center justify-center text-xs font-semibold text-amber-800">
              <Users2 className="mr-1.5 h-3.5 w-3.5" /> {audience == null ? "…" : audience} SMME{audience === 1 ? "" : "s"} will see this
            </div>
            <RecipientsReveal recipients={recipients} testid="notice-recipients" />
          </div>
          <Button onClick={post} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="post-notice-button"><Plus className="h-4 w-4" /> Post notice</Button>
        </div>
      </div>
      <div className="lg:col-span-3 space-y-3" data-testid="buyer-notices-list">
        {notices.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center text-slate-500">No notices posted yet.</p>}
        {notices.map((n) => (
          <div key={n.id} className="flex items-start justify-between rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`buyer-notice-${n.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-display font-bold text-slate-900">{n.title}</h4>
                <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{n.category || "Announcements"}</span>
              </div>
              <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{n.body}</p>
              {((n.target_towns || []).length > 0 || (n.target_sectors || []).length > 0) && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`notice-targets-${n.id}`}>
                  {(n.target_towns || []).map((t) => <span key={t} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700"><MapPin className="h-3 w-3" /> {t}</span>)}
                  {(n.target_sectors || []).map((t) => <span key={t} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700"><Briefcase className="h-3 w-3" /> {t}</span>)}
                </div>
              )}
              <p className="mt-2 text-xs font-medium text-slate-400">{n.author} · {new Date(n.created_at).toLocaleDateString()}{((n.target_towns || []).length === 0 && (n.target_sectors || []).length === 0) ? " · All SMMEs" : " · Targeted"}</p>
            </div>
            {(user?.role === "admin" || n.author_id === user?.id) && (
              <Button size="sm" variant="ghost" onClick={() => del(n.id)} className="text-red-600" data-testid={`delete-notice-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Notifications send ---------- */
function NotificationsSend({ suppliers, history, reload }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [allApproved, setAllApproved] = useState(true);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Subject and message are required"); return; }
    if (!allApproved && selected.length === 0) { toast.error("Select at least one SMME"); return; }
    setSending(true);
    try {
      const payload = allApproved ? { all_approved: true, subject, message } : { recipient_ids: selected, subject, message };
      const r = await api.post("/notifications/send", payload);
      toast.success(`Sent to ${r.data.sent} of ${r.data.total} SMME(s)`);
      setSubject(""); setMessage(""); setSelected([]);
      if (reload) await reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Mail className="h-5 w-5 text-amber-600" /> Send email to SMMEs</h3>
          <p className="text-sm text-slate-500">A real email is delivered to each selected supplier.</p>
          <div className="mt-4 space-y-3">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="notify-subject-input" />
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} placeholder="Write your message…" data-testid="notify-message-input" />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={allApproved} onCheckedChange={(v) => setAllApproved(!!v)} data-testid="notify-all-approved" />
              Send to all approved SMMEs
            </label>
            <Button onClick={send} disabled={sending} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="send-notification-button">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send notification
            </Button>
          </div>
        </div>
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h4 className="font-display font-bold text-slate-900">Recipients</h4>
            {!allApproved && <span className="text-xs text-slate-500">{selected.length} selected</span>}
          </div>
          {allApproved ? (
            <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-500">Sending to all <strong>approved</strong> SMMEs. Uncheck the box to pick individually.</p>
          ) : (
            <div className="mt-3 max-h-80 space-y-1 overflow-y-auto" data-testid="recipient-list">
              {suppliers.map((s) => (
                <label key={s.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50" data-testid={`recipient-${s.id}`}>
                  <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`recipient-check-${s.id}`} />
                  <span className="flex-1 truncate text-slate-700">{s.company_name}</span>
                  <StatusBadge status={s.status} />
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sent history / delivery receipts */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="notification-history">
        <h4 className="flex items-center gap-2 font-display font-bold text-slate-900"><MailCheck className="h-5 w-5 text-amber-600" /> Sent history &amp; delivery receipts</h4>
        <p className="text-xs text-slate-400">Opens are tracked when an SMME's email app loads images; "received" is always accurate.</p>
        <div className="mt-4 space-y-3">
          {(!history || history.length === 0) && <p className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-slate-500" data-testid="no-history">No notifications sent yet.</p>}
          {(history || []).map((n) => (
            <div key={n.id} className="rounded-xl border border-slate-200 p-4" data-testid={`history-${n.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h5 className="font-semibold text-slate-900">{n.subject}</h5>
                  <p className="mt-0.5 text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-800" data-testid={`history-received-${n.id}`}>{n.sent_count} received</span>
                  <span className="rounded-full bg-indigo-100 px-2.5 py-1 font-semibold text-indigo-800" data-testid={`history-opened-${n.id}`}>{n.opened_count} opened</span>
                  {n.sent_count < n.total_count && <span className="rounded-full bg-red-100 px-2.5 py-1 font-semibold text-red-800">{n.total_count - n.sent_count} failed</span>}
                  <Button size="sm" variant="outline" onClick={() => setOpenDetail(openDetail === n.id ? null : n.id)} data-testid={`history-toggle-${n.id}`}>{openDetail === n.id ? "Hide" : "Details"}</Button>
                </div>
              </div>
              {openDetail === n.id && (
                <div className="mt-3 overflow-hidden rounded-lg border border-slate-100">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr><th className="px-4 py-2">SMME</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Received</th><th className="px-4 py-2">Opened</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(n.recipients || []).map((r) => (
                        <tr key={r.supplier_id} data-testid={`history-recipient-${n.id}-${r.supplier_id}`}>
                          <td className="px-4 py-2 font-medium text-slate-800">{r.company_name}</td>
                          <td className="px-4 py-2 text-slate-500">{r.email}</td>
                          <td className="px-4 py-2">{r.status === "sent" ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="h-3.5 w-3.5" /> Yes</span> : <span className="text-red-600">Failed</span>}</td>
                          <td className="px-4 py-2">{r.opened ? <span className="inline-flex items-center gap-1 text-indigo-700"><MailCheck className="h-3.5 w-3.5" /> {new Date(r.opened_at).toLocaleDateString()}</span> : <span className="text-slate-400">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
